import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../../App";
import { useLanguage } from "../../i18n/LanguageContext";
import { isClinicNavEnabled } from "../../routing/b2cAccess";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import BrandLockup from "../../components/BrandLockup";
import ClinicalThemeToggle from "../../components/ClinicalThemeToggle";
import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  ChevronDown,
  History,
  User,
  LogOut,
  Sparkles,
  Menu,
  Plus,
  PanelLeftClose,
  PanelLeft,
  Shield,
  HeartPulse,
} from "lucide-react";
import { councilMemberLabel, unlistedCouncilMember } from "./perspectiveLabel";
import { useSession } from "../../SessionContext";
import SubjectSwitcher from "../../components/SubjectSwitcher";
import { useSubjects } from "../../SubjectContext";

const SIDEBAR_COLLAPSED_KEY = "mc_sidebar_collapsed";

const readSidebarCollapsed = () => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
};

const writeSidebarCollapsed = (collapsed) => {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Storage unavailable (private mode, quota, etc.) — UI state still updates.
  }
};

const Sidebar = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [org, setOrg] = useState(null);
  const [modelConfig, setModelConfig] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const { hasActiveSession, loading: sessionLoading, resetSession, selectedCouncil, result } = useSession();
  const { activeSubjectId } = useSubjects();

  useEffect(() => {
    writeSidebarCollapsed(collapsed);
  }, [collapsed]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.org_id) {
        setOrg(null);
        setModelConfig(null);
        return;
      }
      try {
        const [orgRes, configRes] = await Promise.all([
          axios.get(`${API}/organizations/current`),
          axios.get(`${API}/settings/models`),
        ]);
        setOrg(orgRes.data?.data);
        setModelConfig(configRes.data?.data);
      } catch (error) {
        console.error("Failed to fetch org data:", error);
      }
    };
    fetchData();
  }, [user?.org_id]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navItems = [
    { href: "/case/new", icon: Sparkles, label: t("nav.newCase") },
    { href: "/cases", icon: History, label: t("nav.myCases") },
    ...(activeSubjectId
      ? [{
          href: `/people/${activeSubjectId}`,
          icon: HeartPulse,
          label: t("nav.healthProfile"),
        }]
      : []),
    ...(isClinicNavEnabled("/platform", user)
      ? [{ href: "/platform", icon: Shield, label: t("nav.platform") }]
      : []),
  ];

  const personas = modelConfig?.personas ?? [];
  const used = Array.isArray(result?.council_used) ? result.council_used : null;
  const pickedIds = Array.isArray(selectedCouncil) ? selectedCouncil : [];
  const activeCouncil = used
    ? used.map((member) => {
        const known = personas.find((p) => p.id === member.id);
        return known || unlistedCouncilMember(member);
      })
    : pickedIds
        .map((id) => personas.find((p) => p.id === id))
        .filter((p) => p && p.enabled && !p.chairman_eligible);
  const showAutomaticCouncil = !used && pickedIds.length === 0;

  const isActive = (href) =>
    location.pathname === href
    || (href.startsWith("/people/") && location.pathname.startsWith("/people/"));

  const renderSidebarContent = (isCollapsed) => {
    const navLinkClass = (href) => {
      const active = isActive(href);
      if (isCollapsed) {
        return `flex items-center justify-center w-10 h-10 mx-auto rounded-md transition-colors ${
          active
            ? "text-primary border-l-2 border-primary bg-primary/5"
            : "text-muted-foreground hover:text-foreground hover:bg-secondary"
        }`;
      }
      return `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors border-l-2 ${
        active
          ? "border-primary text-primary bg-primary/5"
          : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary"
      }`;
    };

    const NavLink = ({ item, showDot }) => {
      const link = (
        <Link to={item.href} className={navLinkClass(item.href)}>
          <div className="relative">
            <item.icon className="w-4 h-4" />
            {showDot && (
              <span
                className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${
                  sessionLoading ? "bg-amber-400 animate-pulse" : "bg-consensus"
                }`}
              />
            )}
          </div>
          {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
        </Link>
      );

      if (isCollapsed) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right">{item.label}</TooltipContent>
          </Tooltip>
        );
      }
      return link;
    };

    return (
      <div
        className={`${isCollapsed ? "w-16" : "w-64"} h-full bg-card border-r border-border flex flex-col transition-[width] duration-200`}
        data-testid="sidebar"
        data-collapsed={isCollapsed}
      >
        <div className={`border-b border-border flex items-center ${isCollapsed ? "p-2 justify-center flex-col gap-2" : "p-4 justify-between"}`}>
          {isCollapsed ? (
            <Link
              to="/case/new"
              className="flex items-center justify-center rounded-md hover:opacity-90 transition-opacity"
            >
              <BrandLockup showWordmark={false} markSize={32} />
            </Link>
          ) : (
            <Link to="/case/new" className="flex items-center gap-3 rounded-md hover:opacity-90 transition-opacity min-w-0 flex-1">
              <div className="flex-1 min-w-0">
                <BrandLockup
                  as="h1"
                  markSize={32}
                  className="text-xl tracking-tight font-bold leading-tight"
                  wordmarkClassName="truncate"
                />
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {org?.name || t("nav.secondOpinion")}
                </p>
              </div>
            </Link>
          )}
          {!mobileOpen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => !c)}
                  className="flex-shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  aria-label={isCollapsed ? t("sidebar.expand") : t("sidebar.collapse")}
                  data-testid="sidebar-collapse-toggle"
                >
                  {isCollapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {isCollapsed ? t("sidebar.expand") : t("sidebar.collapse")}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {!isCollapsed && (
          <div className="px-2 py-2 border-b border-border">
            <SubjectSwitcher />
          </div>
        )}

        {modelConfig?.personas && !isCollapsed && (
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs font-medium text-muted-foreground mb-2">{t("nav.activeCouncil")}</p>
            {showAutomaticCouncil ? (
              <p className="text-xs text-muted-foreground" data-testid="sidebar-council-automatic">
                {t("query.councilAutomatic")}
              </p>
            ) : activeCouncil.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                {activeCouncil.map((p) => {
                  const label = councilMemberLabel(p, t);
                  return (
                  <div key={p.id} className="flex items-center gap-1.5" title={label}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0 bg-medical" />
                    <span className="text-xs text-medical truncate">{label}</span>
                  </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{t("settings.councilNotConfigured")}</p>
            )}
          </div>
        )}

        <nav className={`flex-1 ${isCollapsed ? "p-2 space-y-1" : "p-3 space-y-1"}`}>
          {navItems.map((item) => {
            const isQuery = item.href === "/case/new";
            return <NavLink key={item.href} item={item} showDot={isQuery && hasActiveSession} />;
          })}

          {hasActiveSession &&
            (isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    data-testid="sidebar-new-query-btn"
                    onClick={() => {
                      if (sessionLoading) {
                        const ok = window.confirm(t("query.confirmNewQuery"));
                        if (!ok) return;
                      }
                      resetSession();
                    }}
                    className="flex items-center justify-center w-10 h-10 mx-auto rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{t("nav.newQuery")}</TooltipContent>
              </Tooltip>
            ) : (
              <button
                data-testid="sidebar-new-query-btn"
                onClick={() => {
                  if (sessionLoading) {
                    const ok = window.confirm(t("query.confirmNewQuery"));
                    if (!ok) return;
                  }
                  resetSession();
                }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md w-full text-left border-l-2 border-transparent text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">{t("nav.newQuery")}</span>
              </button>
            ))}

        </nav>

        <div className={`border-t border-border ${isCollapsed ? "p-2" : "p-3"}`}>
          {!isCollapsed && (
            <div className="mb-2 flex items-center justify-between gap-2">
              <LanguageSwitcher />
              <ClinicalThemeToggle compact />
            </div>
          )}
          {isCollapsed && (
            <div className="mb-2 flex flex-col items-center gap-2">
              <ClinicalThemeToggle compact />
            </div>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={`${isCollapsed ? "w-10 h-10 p-0 mx-auto justify-center" : "w-full justify-start gap-3 h-auto py-2"} text-muted-foreground hover:text-foreground`}
              >
                <Avatar className="w-8 h-8 border border-border">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-secondary text-foreground text-sm">
                    {user?.name?.[0] || "U"}
                  </AvatarFallback>
                </Avatar>
                {!isCollapsed && (
                  <>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium truncate text-foreground">{user?.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isCollapsed ? "start" : "end"} side={isCollapsed ? "right" : "top"} className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/account" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {t("nav.account")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                {t("nav.signOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <button
        data-testid="mobile-menu-btn"
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-50 w-10 h-10 bg-card border border-border rounded-md flex items-center justify-center"
        aria-label={t("sidebar.openMenu")}
      >
        <Menu className="w-5 h-5 text-muted-foreground" />
      </button>

      <div className="hidden md:block h-screen flex-shrink-0">{renderSidebarContent(collapsed)}</div>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative h-full w-64 animate-in slide-in-from-left duration-200">
            {renderSidebarContent(false)}
          </div>
        </div>
      )}
    </TooltipProvider>
  );
};

export default Sidebar;
